/**
 * Passwordless sign-in and sign-up: address -> 6-digit code -> session.
 *
 * Sign-in can use email or, when the server has it switched on, WhatsApp to a
 * VERIFIED number. Sign-up is email-first, and offers optional WhatsApp
 * verification of the mobile on the details screen (decided 6-7 Oct).
 *
 * Login and signup are genuinely distinct flows now, but they share every
 * screen except the last one, so they share this component and differ by
 * `mode`:
 *
 *   login   destination -> code -> session                     -> /
 *   signup  destination -> code -> profile -> session          -> /
 *
 * Both land home. Signup used to end on a web card-activation page; linking a
 * card is app-only now, so the site does not ask for one.
 *
 * `mode` is sent to the server as `purpose`, which is what makes the two
 * different: /otp/request answers 404 for a login against an address it does
 * not know and 409 for a signup against one it does. Neither is a dead end -
 * both put the visitor one click away from the other flow with the address they
 * already typed carried along.
 */

import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Form, FormFeedback } from 'reactstrap';
import { toast } from 'react-toastify';
import { AlertCircle, ArrowRight } from 'react-feather';
import * as yup from 'yup';

import Input from 'shared/Input/Input';
import Select from 'shared/Select/Select';
import ButtonPrimary from 'shared/Button/ButtonPrimary';
import {
    authErrorMessage,
    authErrorStatus,
    completeOtpRegistration,
    fetchCountryCodes,
    fetchOtpChannels,
    getKnownOtpChannels,
    CountryCode,
    DEFAULT_DIAL_CODE,
    isAccountExistsError,
    isAccountMissingError,
    isChannelUnavailableError,
    isEmailFallbackError,
    isMobileTakenError,
    isMobileVerificationInvalidError,
    isRegistrationResponse,
    isThrottledError,
    requestOtp,
    throttleRetryAfterSeconds,
    verifyOtp,
    AuthSessionResponse,
    OtpChannel,
    OtpPurpose
} from 'service/authApi';
import useAuthSession from 'hooks/useAuthSession';
import WhatsAppVerifyPanel from './WhatsAppVerifyPanel';

/** Matches the backend RESEND_INTERVAL_SECONDS. The server enforces it; this is only the countdown. */
const RESEND_SECONDS = 60;
const CODE_LENGTH = 6;

/**
 * Exactly ten digits, matching the app's `RegexHelper.tenDigitMobile`.
 *
 * Was `/^(\+?91[-\s]?)?[6-9]\d{9}$/`, which accepted a `+91` typed into the box
 * in three different spellings and pinned the first digit to 6-9. Both had to
 * go once a dial-code picker exists beside the field: the prefix is now chosen,
 * not typed, and a 6-9 rule is a statement about Indian numbering that has no
 * business applying to the other 249 countries in the list.
 */
const MOBILE_PATTERN = /^\d{10}$/;
const MOBILE_LENGTH = 10;

export type OtpAuthMode = OtpPurpose;

type Step = 'destination' | 'code' | 'profile';

/**
 * Router state used to hand a typed address between /login and /signup, so a
 * visitor who guessed the wrong one does not retype it. Deliberately router
 * state rather than a query string: the address stays out of the URL bar,
 * browser history and any analytics that reads `location.search`.
 */
export interface AuthHandoffState {
    email?: string;
}

const onlyDigits = (value: string, max = CODE_LENGTH) => value.replace(/\D/g, '').slice(0, max);

// ---------------------------------------------------------------------------
// The "you are on the wrong page" panel
// ---------------------------------------------------------------------------

interface CrossLinkNotice {
    /** Always the server wording - it is written for the visitor and we do not second-guess it. */
    message: string;
    cta: string;
    /** Either a route to go to (the other flow, address carried along)... */
    to?: string;
    /** Carried into the other flow so the address survives the click. */
    email?: string;
    /** ...or something to do on this page, e.g. switch from WhatsApp to email. */
    onAction?: () => void;
}

const CrossLinkPanel: FC<{ notice: CrossLinkNotice }> = ({ notice }) => (
    <div
        role="alert"
        className="flex gap-3 rounded-2xl border border-neutral-200 bg-neutral-50 p-4 dark:border-neutral-700 dark:bg-neutral-800"
    >
        <AlertCircle size={20} className="mt-0.5 flex-shrink-0 text-green-600" aria-hidden="true" />
        <div className="min-w-0">
            <p className="text-sm text-neutral-700 dark:text-neutral-200">{notice.message}</p>
            {notice.onAction ? (
                <button
                    type="button"
                    onClick={notice.onAction}
                    className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-green-600 hover:underline"
                >
                    {notice.cta}
                    <ArrowRight size={16} aria-hidden="true" />
                </button>
            ) : (
                <Link
                    to={notice.to || '/login'}
                    state={{ email: notice.email } as AuthHandoffState}
                    className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-green-600 hover:underline"
                >
                    {notice.cta}
                    <ArrowRight size={16} aria-hidden="true" />
                </Link>
            )}
        </div>
    </div>
);

// ---------------------------------------------------------------------------
// Email / WhatsApp switch - login only
// ---------------------------------------------------------------------------

/**
 * Shown only on the login page and only when the server reports WhatsApp as
 * live. Signup is email-first by decision, so it never offers this.
 */
const ChannelSwitch: FC<{ channel: OtpChannel; onChange: (channel: OtpChannel) => void; disabled: boolean }> = ({
    channel,
    onChange,
    disabled
}) => {
    const option = (value: OtpChannel, label: string) => (
        <button
            type="button"
            role="tab"
            aria-selected={channel === value}
            disabled={disabled}
            onClick={() => onChange(value)}
            className={`flex-1 rounded-full py-2 text-sm font-medium transition-colors ${
                channel === value
                    ? 'bg-white text-neutral-900 shadow dark:bg-neutral-700 dark:text-neutral-100'
                    : 'text-neutral-500 hover:text-neutral-800 dark:text-neutral-400 dark:hover:text-neutral-200'
            }`}
        >
            {label}
        </button>
    );

    return (
        <div role="tablist" aria-label="Receive your code by" className="flex gap-1 rounded-full bg-neutral-100 p-1 dark:bg-neutral-800">
            {option('email', 'Email')}
            {option('whatsapp', 'WhatsApp')}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Step 1 - the address
// ---------------------------------------------------------------------------

type DestinationFormValues = { email: string };

const destinationSchema = yup.object().shape({
    email: yup.string().trim().email('Must be a valid email.').required('Email is required')
});

interface DestinationStepProps {
    mode: OtpAuthMode;
    defaultEmail: string;
    busy: boolean;
    onSubmitEmail: (email: string) => void;
}

const DestinationStep: FC<DestinationStepProps> = ({ mode, defaultEmail, busy, onSubmitEmail }) => {
    const {
        register,
        handleSubmit,
        formState: { errors }
    } = useForm<DestinationFormValues>({
        mode: 'onChange',
        resolver: yupResolver(destinationSchema),
        defaultValues: { email: defaultEmail }
    });

    const onSubmit: SubmitHandler<DestinationFormValues> = (data) => onSubmitEmail(data.email);

    return (
        <Form className="grid grid-cols-1 gap-6" action="#" method="post" onSubmit={handleSubmit(onSubmit)}>
            <label className="block">
                <span className="text-neutral-800 dark:text-neutral-200">Email address</span>
                <Input
                    type="email"
                    autoComplete="email"
                    placeholder="example@example.com"
                    className="mt-1"
                    style={{ borderColor: errors.email ? '#ea5455' : '' }}
                    {...register('email')}
                />
                {errors && errors.email && <FormFeedback style={{ color: '#ea5455' }}>{errors.email.message}</FormFeedback>}
                <span className="block mt-2 text-sm text-neutral-500 dark:text-neutral-400">
                    {mode === 'signup'
                        ? 'We will email you a 6-digit code to confirm the address, then ask for a few details. No password needed.'
                        : 'We will email you a 6-digit code to sign in. No password needed.'}
                </span>
            </label>
            <ButtonPrimary type="submit" disabled={busy} loading={busy}>
                {busy ? 'Sending code...' : mode === 'signup' ? 'Send Verification Code' : 'Login with OTP'}
            </ButtonPrimary>
        </Form>
    );
};

/**
 * Step 1, WhatsApp variant: a 10-digit Indian number. The +91 is fixed, not a
 * picker -- the server only sends WhatsApp codes to Indian numbers, and offering
 * 249 other countries would only produce a failure later.
 */
type MobileDestinationFormValues = { mobile: string };

const mobileDestinationSchema = yup.object().shape({
    mobile: yup
        .string()
        .trim()
        .required('Mobile number is required.')
        .matches(MOBILE_PATTERN, `Enter a ${MOBILE_LENGTH} digit mobile number.`)
});

interface MobileDestinationStepProps {
    defaultMobile: string;
    busy: boolean;
    onSubmitMobile: (mobile: string) => void;
}

const MobileDestinationStep: FC<MobileDestinationStepProps> = ({ defaultMobile, busy, onSubmitMobile }) => {
    const {
        register,
        handleSubmit,
        formState: { errors }
    } = useForm<MobileDestinationFormValues>({
        mode: 'onChange',
        resolver: yupResolver(mobileDestinationSchema),
        defaultValues: { mobile: defaultMobile }
    });

    const onSubmit: SubmitHandler<MobileDestinationFormValues> = (data) => onSubmitMobile(data.mobile);

    return (
        <Form className="grid grid-cols-1 gap-6" action="#" method="post" onSubmit={handleSubmit(onSubmit)}>
            <label className="block">
                <span className="text-neutral-800 dark:text-neutral-200">WhatsApp number</span>
                <div className="mt-1 flex">
                    <span className="inline-flex items-center rounded-l-2xl border border-r-0 border-neutral-200 bg-neutral-50 px-3 text-sm text-neutral-500 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-400">
                        +91
                    </span>
                    <Input
                        type="tel"
                        inputMode="numeric"
                        autoComplete="tel-national"
                        placeholder="9876543210"
                        maxLength={MOBILE_LENGTH}
                        autoFocus
                        className="!rounded-l-none"
                        style={{ borderColor: errors.mobile ? '#ea5455' : '' }}
                        {...register('mobile')}
                        // Strip non-digits as typed, so a pasted "+91 98765 43210"
                        // becomes a number rather than a validation error.
                        onChange={(event) => {
                            const digits = event.target.value.replace(/\D/g, '');
                            event.target.value = (digits.length > MOBILE_LENGTH ? digits.slice(-MOBILE_LENGTH) : digits).slice(0, MOBILE_LENGTH);
                            register('mobile').onChange(event);
                        }}
                    />
                </div>
                {errors && errors.mobile && <FormFeedback style={{ color: '#ea5455' }}>{errors.mobile.message}</FormFeedback>}
                <span className="mt-2 block text-sm text-neutral-500 dark:text-neutral-400">
                    We will send a 6-digit code to your WhatsApp. This works for a number you have verified on your InstaaCard account.
                </span>
            </label>
            <ButtonPrimary type="submit" disabled={busy} loading={busy}>
                {busy ? 'Sending code...' : 'Send code on WhatsApp'}
            </ButtonPrimary>
        </Form>
    );
};

// ---------------------------------------------------------------------------
// Step 2 - the code
// ---------------------------------------------------------------------------

interface CodeStepProps {
    mode: OtpAuthMode;
    /** Where the code went, as shown to the visitor. */
    email: string;
    channel: OtpChannel;
    busy: boolean;
    error: string;
    resendIn: number;
    onVerify: (code: string) => void;
    onResend: () => void;
    onChangeEmail: () => void;
    /** WhatsApp only: the way out when the code does not arrive. */
    onUseEmail?: () => void;
}

const CodeStep: FC<CodeStepProps> = ({ mode, email, channel, busy, error, resendIn, onVerify, onResend, onChangeEmail, onUseEmail }) => {
    const viaWhatsApp = channel === 'whatsapp';
    const [code, setCode] = useState('');
    // Auto-submitting whatever the OS or the clipboard just filled in is a real
    // convenience; doing it on every sixth keystroke is not. A typo would
    // silently burn one of the five attempts before the visitor could correct
    // it. So only a jump of more than one digit - a paste or an autofill -
    // submits by itself; typed codes always wait for the button.
    const autoSubmittedRef = useRef<string>('');

    const applyValue = (next: string, previous: string) => {
        setCode(next);
        if (next.length === CODE_LENGTH && next.length - previous.length > 1 && autoSubmittedRef.current !== next) {
            autoSubmittedRef.current = next;
            onVerify(next);
        }
    };

    const submit = (event: React.FormEvent) => {
        event.preventDefault();
        if (code.length === CODE_LENGTH) onVerify(code);
    };

    return (
        <form className="grid grid-cols-1 gap-6" onSubmit={submit}>
            <div className="text-center text-sm text-neutral-600 dark:text-neutral-400">
                {viaWhatsApp ? 'Enter the 6-digit code we sent to your WhatsApp on' : 'Enter the 6-digit code we sent to'}
                <span className="block font-medium text-neutral-900 dark:text-neutral-100 break-all">{viaWhatsApp ? `+91 ${email}` : email}</span>
                <span className="block mt-1">The code expires in 10 minutes.</span>
            </div>

            <label className="block">
                <span className="text-neutral-800 dark:text-neutral-200">Verification code</span>
                <Input
                    type="text"
                    // `one-time-code` is what makes mobile browsers offer the
                    // code straight from the email/SMS notification, and the
                    // numeric hint keeps the keypad on screen.
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={CODE_LENGTH}
                    autoFocus
                    placeholder="123456"
                    value={code}
                    onChange={(event) => applyValue(onlyDigits(event.target.value), code)}
                    onPaste={(event) => {
                        // Codes get copied out of emails with stray spaces
                        // around them, and a paste into a half-filled box
                        // should replace rather than append.
                        const pasted = event.clipboardData.getData('text');
                        if (!pasted) return;
                        event.preventDefault();
                        applyValue(onlyDigits(pasted), code);
                    }}
                    // Explicit size/font classes rather than extra utilities on
                    // top of the defaults: a 6-digit code needs more room than
                    // the h-11/text-sm an ordinary field gets.
                    sizeClass="h-14 px-4"
                    fontClass="text-2xl font-medium"
                    className="mt-1 text-center"
                    style={{
                        borderColor: error ? '#ea5455' : '',
                        // The tracking adds a gap after the last digit too, so
                        // the indent puts the run of digits back in the middle.
                        letterSpacing: '0.5em',
                        textIndent: '0.5em'
                    }}
                />
                {error && <FormFeedback style={{ color: '#ea5455' }}>{error}</FormFeedback>}
            </label>

            <ButtonPrimary type="submit" disabled={busy || code.length !== CODE_LENGTH} loading={busy}>
                {busy ? 'Verifying...' : mode === 'signup' ? 'Verify and continue' : 'Verify and sign in'}
            </ButtonPrimary>

            <div className="flex items-center justify-between text-sm">
                <button type="button" className="text-green-600 hover:underline" onClick={onChangeEmail} disabled={busy}>
                    {viaWhatsApp ? 'Change number' : 'Change email'}
                </button>
                <button
                    type="button"
                    className="text-green-600 hover:underline disabled:text-neutral-400 disabled:no-underline disabled:cursor-not-allowed dark:disabled:text-neutral-500"
                    onClick={onResend}
                    disabled={busy || resendIn > 0}
                >
                    {resendIn > 0 ? `Resend code in ${resendIn}s` : 'Resend code'}
                </button>
            </div>

            {viaWhatsApp && onUseEmail && (
                <p className="text-center text-sm text-neutral-500 dark:text-neutral-400">
                    Didn&apos;t get it?{' '}
                    <button type="button" className="font-medium text-green-600 hover:underline" onClick={onUseEmail} disabled={busy}>
                        Sign in with email instead
                    </button>
                </p>
            )}
        </form>
    );
};

// ---------------------------------------------------------------------------
// Step 3 - profile, signup only
// ---------------------------------------------------------------------------

type ProfileFormValues = {
    firstName: string;
    lastName: string;
    email: string;
    mobile: string;
    /** The picked dial code, sent next to the mobile. Not a form field - set on submit. */
    countryCode?: string;
};

/**
 * Both contact fields are required. The one the code arrived on is proven and
 * shown read-only; the other is what this step is really for, and the backend
 * stores it unverified.
 */
const profileSchema = yup.object().shape({
    firstName: yup.string().trim().required('First name is required.'),
    lastName: yup.string().trim().required('Last name is required.'),
    email: yup.string().trim().email('Must be a valid email.').required('Email is required.'),
    mobile: yup
        .string()
        .trim()
        .required('Mobile number is required.')
        .matches(MOBILE_PATTERN, `Enter a ${MOBILE_LENGTH} digit mobile number.`)
});

/** A field the visitor cannot edit because the code they just typed proved it. */
const VERIFIED_INPUT_CLASS = 'mt-1 bg-neutral-100 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400';

/** A WhatsApp verification of the signup mobile, and the number it proved. */
export interface MobileProof {
    token: string;
    mobile: string;
}

interface ProfileStepProps {
    /** The address the code went to. */
    destination: string;
    /** Which of the two contact fields `destination` fills in. */
    channel: OtpChannel;
    busy: boolean;
    onSubmitProfile: (values: ProfileFormValues, mobileVerificationToken?: string) => void;
    /** Proves who is verifying the mobile; see WhatsAppVerifyPanel. */
    registrationToken: string;
    /** Whether the server has WhatsApp switched on. */
    whatsappAvailable: boolean;
    /** Held by the orchestrator so a rejected proof can be cleared from there. */
    mobileProof: MobileProof | null;
    onMobileProof: (proof: MobileProof | null) => void;
}

const ProfileStep: FC<ProfileStepProps> = ({
    destination,
    channel,
    busy,
    onSubmitProfile,
    registrationToken,
    whatsappAvailable,
    mobileProof,
    onMobileProof
}) => {
    const emailVerified = channel === 'email';
    const [verifyOpen, setVerifyOpen] = useState(false);

    // Dial codes for the picker beside the mobile field.
    //
    // Held here rather than in a store because this is the only screen on the
    // site that collects a mobile number against a country. The list is only
    // fetched when the field is actually editable - when the code went to the
    // phone, the number is already proven and read-only, so there is nothing to
    // pick and no reason to pull 250 rows.
    const [countries, setCountries] = useState<CountryCode[]>([]);
    const [dialCode, setDialCode] = useState<string>(DEFAULT_DIAL_CODE);

    useEffect(() => {
        if (!emailVerified) return;
        let cancelled = false;

        fetchCountryCodes().then((rows) => {
            // The component can unmount while 250 rows are in flight; setting
            // state afterwards is a no-op React warns about.
            if (cancelled) return;
            setCountries(rows);
        });

        return () => {
            cancelled = true;
        };
    }, [emailVerified]);

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors }
    } = useForm<ProfileFormValues>({
        mode: 'onChange',
        resolver: yupResolver(profileSchema),
        defaultValues: {
            firstName: '',
            lastName: '',
            email: emailVerified ? destination : '',
            mobile: emailVerified ? '' : destination
        }
    });

    const mobileValue = (watch('mobile') || '').trim();

    // WhatsApp verification is offered only for a complete Indian number while
    // the server has WhatsApp on -- the server sends to Indian numbers only. It
    // is optional (decided 7 Oct): nothing here blocks "Create account".
    const canVerifyMobile = emailVerified && whatsappAvailable && dialCode === DEFAULT_DIAL_CODE && MOBILE_PATTERN.test(mobileValue);

    // A proof counts only for the exact number it was minted for. Should the
    // dial code move off +91 it stops counting -- and is not sent, since the
    // server would refuse it anyway.
    const mobileProven = Boolean(mobileProof && mobileProof.mobile === mobileValue && dialCode === DEFAULT_DIAL_CODE);

    // The verified field is read-only rather than disabled so react-hook-form
    // still sees its value, but the submitted value comes from `destination`
    // either way - a read-only input is still a DOM node somebody can edit.
    const onSubmit: SubmitHandler<ProfileFormValues> = (data) =>
        onSubmitProfile(
            {
                ...data,
                email: emailVerified ? destination : data.email,
                mobile: emailVerified ? data.mobile : destination,
                countryCode: emailVerified ? dialCode : DEFAULT_DIAL_CODE
            },
            mobileProven && mobileProof ? mobileProof.token : undefined
        );

    return (
        <Form className="grid grid-cols-1 gap-5" action="#" method="post" onSubmit={handleSubmit(onSubmit)}>
            <div className="text-center text-sm text-neutral-600 dark:text-neutral-400">
                <span className="block font-medium text-neutral-900 dark:text-neutral-100 break-all">{destination}</span>
                is verified. A few details and your account is ready.
            </div>

            <label className="block">
                <span className="text-neutral-800 dark:text-neutral-200">First name</span>
                <Input
                    type="text"
                    autoComplete="given-name"
                    autoFocus
                    className="mt-1"
                    style={{ borderColor: errors.firstName ? '#ea5455' : '' }}
                    {...register('firstName')}
                />
                {errors && errors.firstName && <FormFeedback style={{ color: '#ea5455' }}>{errors.firstName.message}</FormFeedback>}
            </label>

            <label className="block">
                <span className="text-neutral-800 dark:text-neutral-200">Last name</span>
                <Input
                    type="text"
                    autoComplete="family-name"
                    className="mt-1"
                    style={{ borderColor: errors.lastName ? '#ea5455' : '' }}
                    {...register('lastName')}
                />
                {errors && errors.lastName && <FormFeedback style={{ color: '#ea5455' }}>{errors.lastName.message}</FormFeedback>}
            </label>

            <label className="block">
                <span className="flex justify-between items-center text-neutral-800 dark:text-neutral-200">
                    Email address
                    {emailVerified && <span className="text-sm font-normal text-green-600">Verified</span>}
                </span>
                <Input
                    type="email"
                    autoComplete="email"
                    placeholder="example@example.com"
                    readOnly={emailVerified}
                    aria-readonly={emailVerified}
                    className={emailVerified ? VERIFIED_INPUT_CLASS : 'mt-1'}
                    style={{ borderColor: errors.email ? '#ea5455' : '' }}
                    {...register('email')}
                />
                {errors && errors.email && <FormFeedback style={{ color: '#ea5455' }}>{errors.email.message}</FormFeedback>}
            </label>

            <label className="block">
                <span className="flex justify-between items-center text-neutral-800 dark:text-neutral-200">
                    Mobile
                    {!emailVerified && <span className="text-sm font-normal text-green-600">Verified</span>}
                    {emailVerified && mobileProven && (
                        <span className="text-sm font-normal text-green-600" data-testid="mobile-verified-badge">
                            Verified on WhatsApp
                        </span>
                    )}
                </span>
                {/* Widths are set on these wrappers, not on Select/Input themselves.
                    Both shared components hardcode `block w-full` inside their own class
                    string, which beats a `w-32` passed in via className - Tailwind emits
                    w-full later, so it wins regardless of prop order. That is what
                    collapsed the number box to a circle. Sizing the flex child and letting
                    the component fill it is the fix; `min-w-0` is needed because a flex
                    item defaults to min-width:auto and would otherwise refuse to shrink. */}
                <div className="flex gap-2 mt-1">
                    {/* Only offered while the mobile is the field being collected. When the
                        code arrived on the phone instead, the number is read-only and came
                        from the server complete - there is nothing to pick. Same rule as
                        the app's signup screen. */}
                    {emailVerified && (
                        <div className="flex-none w-28 sm:w-32">
                            <Select
                                aria-label="Country dial code"
                                value={dialCode}
                                onChange={(event) => setDialCode(event.target.value)}
                            >
                                {/* Rendered before the fetch resolves, and the only option if it
                                    fails, so the field is never empty or unusable. */}
                                {!countries.some((country) => country.dial_code === dialCode) && (
                                    <option value={dialCode}>{dialCode}</option>
                                )}
                                {countries.map((country) => (
                                    <option key={country.id} value={country.dial_code}>
                                        {country.dial_code} {country.iso_code}
                                    </option>
                                ))}
                            </Select>
                        </div>
                    )}
                    <div className="flex-1 min-w-0">
                        <Input
                            type="tel"
                            inputMode="numeric"
                            autoComplete="tel"
                            placeholder="9876543210"
                            maxLength={MOBILE_LENGTH}
                            // Locked once proven, so the number cannot drift away
                            // from the proof. "Change number" below unlocks it.
                            readOnly={!emailVerified || mobileProven}
                            aria-readonly={!emailVerified || mobileProven}
                            className={!emailVerified || mobileProven ? VERIFIED_INPUT_CLASS : ''}
                            style={{ borderColor: errors.mobile ? '#ea5455' : '' }}
                            {...register('mobile')}
                            // Strip anything that is not a digit as it is typed, so a pasted
                            // "+91 98765 43210" becomes a number the field can accept instead
                            // of a validation error the visitor has to decipher. Runs before
                            // react-hook-form's own onChange so it sees the cleaned value.
                            onChange={(event) => {
                                const cleaned = event.target.value.replace(/\D/g, '').slice(0, MOBILE_LENGTH);
                                event.target.value = cleaned;
                                register('mobile').onChange(event);
                            }}
                        />
                    </div>
                </div>
                {errors && errors.mobile && <FormFeedback style={{ color: '#ea5455' }}>{errors.mobile.message}</FormFeedback>}
                {emailVerified && (
                    <span className="block mt-2 text-sm text-neutral-500 dark:text-neutral-400">
                        We use this for delivery updates about your card.
                    </span>
                )}
            </label>

            {/* Outside the <label>: a panel inside one would steal focus to the
                input on every click. */}
            {mobileProven && (
                <button
                    type="button"
                    className="-mt-3 justify-self-start text-sm text-green-600 hover:underline"
                    onClick={() => onMobileProof(null)}
                    disabled={busy}
                >
                    Change number
                </button>
            )}

            {canVerifyMobile && !mobileProven && !verifyOpen && (
                <button
                    type="button"
                    className="-mt-3 justify-self-start text-sm font-medium text-green-600 hover:underline"
                    onClick={() => setVerifyOpen(true)}
                    disabled={busy}
                >
                    Verify on WhatsApp (optional)
                </button>
            )}

            {canVerifyMobile && !mobileProven && verifyOpen && (
                <WhatsAppVerifyPanel
                    // A different number is a different verification.
                    key={mobileValue}
                    mobile={mobileValue}
                    caller={{ registrationToken }}
                    context="signup"
                    onVerified={({ mobileVerificationToken }) => {
                        if (mobileVerificationToken) onMobileProof({ token: mobileVerificationToken, mobile: mobileValue });
                        setVerifyOpen(false);
                    }}
                    onDismiss={() => setVerifyOpen(false)}
                />
            )}

            <ButtonPrimary type="submit" disabled={busy} loading={busy}>
                {busy ? 'Creating your account...' : 'Create account'}
            </ButtonPrimary>
        </Form>
    );
};

// ---------------------------------------------------------------------------
// Orchestrator
// ---------------------------------------------------------------------------

export interface OtpAuthFormProps {
    className?: string;
    mode?: OtpAuthMode;
    /** Prefilled when the visitor arrived from the other flow. See AuthHandoffState. */
    defaultEmail?: string;
}

const OtpAuthForm: FC<OtpAuthFormProps> = ({ className = '', mode = 'login', defaultEmail = '' }) => {
    const establishSession = useAuthSession();

    const [step, setStep] = useState<Step>('destination');
    const [destination, setDestination] = useState(defaultEmail);
    const [registrationToken, setRegistrationToken] = useState('');
    const [codeError, setCodeError] = useState('');
    const [resendIn, setResendIn] = useState(0);
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState<CrossLinkNotice | null>(null);

    // Which channels the server has switched on: the answer already known (fetched
    // as the site loaded, or remembered from the last visit), else null until the
    // server answers. The login form waits on null with a short loader rather than
    // drawing an email form that the Email | WhatsApp tabs would then push down.
    const [knownChannels, setKnownChannels] = useState<OtpChannel[] | null>(getKnownOtpChannels);
    const availableChannels: OtpChannel[] = knownChannels ?? ['email'];
    // WhatsApp is a SIGN-IN channel only -- signup stays email-first by
    // decision -- so on the signup page this never leaves 'email'.
    const [channel, setChannel] = useState<OtpChannel>('email');
    // The email typed before switching to WhatsApp, restored on switching back.
    const [lastEmail, setLastEmail] = useState(defaultEmail);
    // Signup: a WhatsApp proof of the mobile, if the visitor chose to verify.
    const [mobileProof, setMobileProof] = useState<MobileProof | null>(null);

    const whatsappAvailable = availableChannels.includes('whatsapp');

    useEffect(() => {
        let cancelled = false;
        // Shares the request index.tsx started; keeps the known answer current.
        fetchOtpChannels().then((channels) => {
            if (cancelled) return;
            setKnownChannels(channels);
            // Switched off since the page opened: back to email, not a dead tab.
            if (!channels.includes('whatsapp')) setChannel((current) => (current === 'whatsapp' ? 'email' : current));
        });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (resendIn <= 0) return;
        const timer = window.setTimeout(() => setResendIn((seconds) => seconds - 1), 1000);
        return () => window.clearTimeout(timer);
    }, [resendIn]);

    const switchChannel = useCallback(
        (next: OtpChannel) => {
            setChannel(next);
            setStep('destination');
            setCodeError('');
            setNotice(null);
            setResendIn(0);
            setDestination(next === 'email' ? lastEmail : '');
        },
        [lastEmail]
    );

    const useEmailInstead = useCallback(() => switchChannel('email'), [switchChannel]);

    const sendCode = useCallback(
        async (target: string) => {
            setBusy(true);
            setCodeError('');
            setNotice(null);
            // Keep whatever was typed even when the request fails - the cross
            // link below needs it, and so does the field they are looking at.
            setDestination(target);
            if (channel === 'email') setLastEmail(target);

            try {
                const data = await requestOtp({ destination: target, purpose: mode, channel: channel });
                setResendIn(RESEND_SECONDS);
                setStep('code');
                toast.success(data?.message || 'Your verification code is on its way.');
            } catch (err) {
                // WhatsApp: an unknown or unverified number, a failed send, the
                // per-number limit, or WhatsApp switched off since the page
                // loaded. Whatever the cause, email is the way forward (decided
                // 7 Oct) - say what the server said and put email one click away.
                if (channel === 'whatsapp' && (isEmailFallbackError(err) || isChannelUnavailableError(err))) {
                    // The gap between sends means a code is already on the
                    // phone - same as the email path, go to the code box.
                    const retryAfter = isThrottledError(err) ? throttleRetryAfterSeconds(err) : undefined;
                    if (retryAfter && retryAfter <= RESEND_SECONDS) {
                        setResendIn(retryAfter);
                        setStep('code');
                        toast.info(authErrorMessage(err));
                        return;
                    }

                    setNotice({
                        message: authErrorMessage(err, 'We could not send a code to your WhatsApp. Please sign in with your email instead.'),
                        cta: 'Sign in with email instead',
                        onAction: useEmailInstead
                    });
                    return;
                }

                // Wrong flow, not a failure. Say what the server said and put
                // the other one a click away, address and all.
                if (mode === 'login' && isAccountMissingError(err)) {
                    setNotice({
                        message: authErrorMessage(err, "We couldn't find an account for that address."),
                        to: '/signup',
                        cta: 'Create an account with this email',
                        email: target
                    });
                    return;
                }

                if (mode === 'signup' && isAccountExistsError(err)) {
                    setNotice({
                        message: authErrorMessage(err, 'An account already exists for that address. Please sign in.'),
                        to: '/login',
                        cta: 'Sign in with this email',
                        email: target
                    });
                    return;
                }

                if (isChannelUnavailableError(err)) {
                    toast.info(authErrorMessage(err, 'That way of receiving codes is not available yet. Please use email.'));
                    return;
                }

                const retryAfter = isThrottledError(err) ? throttleRetryAfterSeconds(err) : undefined;

                if (retryAfter) {
                    // Throttled by the 60-second gap, which means a code went
                    // out moments ago and is already in their inbox - the code
                    // screen is where they want to be. Never re-request
                    // automatically.
                    setResendIn(retryAfter);
                    setStep('code');
                    toast.info(authErrorMessage(err));
                } else {
                    toast.error(authErrorMessage(err, 'We could not send the verification code right now. Please try again.'));
                }
            } finally {
                setBusy(false);
            }
        },
        [mode, channel, useEmailInstead]
    );

    const finish = useCallback(
        async (session: AuthSessionResponse) => {
            toast.success(session?.message || 'Login Success');
            await establishSession(session);
        },
        [establishSession]
    );

    const onVerify = useCallback(
        async (code: string) => {
            setBusy(true);
            setCodeError('');
            try {
                const data = await verifyOtp({ destination: destination, code: code, purpose: mode, channel: channel });

                if (isRegistrationResponse(data)) {
                    setRegistrationToken(data.registrationToken);
                    setMobileProof(null);
                    setStep('profile');
                    return;
                }

                await finish(data as AuthSessionResponse);
            } catch (err) {
                const message = authErrorMessage(err, 'That code was not correct. Please try again.');
                setCodeError(message);
                toast.error(message);
            } finally {
                setBusy(false);
            }
        },
        [destination, mode, channel, finish]
    );

    const onSubmitProfile = useCallback(
        async (values: ProfileFormValues, mobileVerificationToken?: string) => {
            setBusy(true);
            try {
                const data = await completeOtpRegistration({
                    registrationToken: registrationToken,
                    firstName: values.firstName,
                    lastName: values.lastName,
                    email: values.email,
                    mobile: values.mobile,
                    countryCode: values.countryCode,
                    mobileVerificationToken: mobileVerificationToken
                });
                await finish(data);
            } catch (err) {
                // The WhatsApp proof expired, or the number was verified on
                // another account in the meantime. Drop the proof so the field
                // unlocks: verify again, or create the account without it.
                if (isMobileVerificationInvalidError(err) || isMobileTakenError(err)) {
                    setMobileProof(null);
                    toast.error(authErrorMessage(err, 'Please verify your number again, or continue without verifying it.'));
                    return;
                }

                // One account per person: the *other* contact field can turn
                // out to belong to somebody already, and only this call can
                // find that out.
                if (isAccountExistsError(err)) {
                    setNotice({
                        message: authErrorMessage(err, 'An account already exists with those details. Please sign in.'),
                        to: '/login',
                        cta: 'Sign in instead',
                        email: values.email
                    });
                    return;
                }

                toast.error(authErrorMessage(err, 'We could not create your account. Please try again.'));

                // The registration token has a short life of its own; once it
                // has expired the only way forward is a fresh code.
                if (authErrorStatus(err) === 401) {
                    setRegistrationToken('');
                    setMobileProof(null);
                    setCodeError('');
                    setStep('destination');
                }
            } finally {
                setBusy(false);
            }
        },
        [registrationToken, finish]
    );

    const backToDestination = useCallback(() => {
        setStep('destination');
        setCodeError('');
        setNotice(null);
    }, []);

    // Login only: whether WhatsApp is on is not known yet (first visit, slow
    // network). Wait briefly instead of drawing a form that is about to change.
    // fetchOtpChannels gives up after a few seconds and settles on email.
    if (mode === 'login' && knownChannels === null && step === 'destination') {
        return (
            <div className={`flex justify-center py-10 ${className}`} role="status" aria-label="Loading sign-in options" data-testid="otp-channels-loading">
                <span className="h-6 w-6 animate-spin rounded-full border-2 border-neutral-300 border-t-primary-6000" />
            </div>
        );
    }

    return (
        <div className={`space-y-6 ${className}`}>
            {mode === 'login' && whatsappAvailable && step === 'destination' && (
                <ChannelSwitch channel={channel} onChange={switchChannel} disabled={busy} />
            )}

            {notice && <CrossLinkPanel notice={notice} />}

            {step === 'destination' && channel === 'email' && (
                <DestinationStep mode={mode} defaultEmail={destination} busy={busy} onSubmitEmail={sendCode} />
            )}

            {step === 'destination' && channel === 'whatsapp' && (
                <MobileDestinationStep defaultMobile={destination} busy={busy} onSubmitMobile={sendCode} />
            )}

            {step === 'code' && (
                <CodeStep
                    // Remounting on a new address clears a half-typed code,
                    // which is what the visitor expects after changing it.
                    key={`${channel}:${destination}`}
                    mode={mode}
                    email={destination}
                    channel={channel}
                    busy={busy}
                    error={codeError}
                    resendIn={resendIn}
                    onVerify={onVerify}
                    onResend={() => sendCode(destination)}
                    onChangeEmail={backToDestination}
                    onUseEmail={channel === 'whatsapp' ? useEmailInstead : undefined}
                />
            )}

            {step === 'profile' && (
                <ProfileStep
                    destination={destination}
                    channel={channel}
                    busy={busy}
                    onSubmitProfile={onSubmitProfile}
                    registrationToken={registrationToken}
                    whatsappAvailable={whatsappAvailable}
                    mobileProof={mobileProof}
                    onMobileProof={setMobileProof}
                />
            )}
        </div>
    );
};

export default OtpAuthForm;
